/**
 * B632 (e2) — the guest's ADD sheet: the pure half. Which ticket types can
 * be added (the booking's show, priced on the channel — an unpriced type is
 * the box office's), and the summary before the hold. The kernel's total
 * (fees and tax) arrives with the hold and is the one PAY carries.
 */
import { formatMoney } from "./money.js";

/** One variant as the buyer card (`catalog.get`) carries it. */
export interface MeAddVariant {
  readonly variantId: string;
  readonly name: string;
  readonly displayPrice?: { readonly amount: number; readonly currency: string } | undefined;
}

export interface MeAddChoice {
  readonly variantId: string;
  readonly name: string;
  /** "$25.00". */
  readonly price: string;
  readonly priceMinor: number;
}

export function meAddChoices(variants: readonly MeAddVariant[]): readonly MeAddChoice[] {
  return variants.flatMap((v) =>
    v.displayPrice === undefined ? [] : [{ variantId: v.variantId, name: v.name, price: formatMoney(v.displayPrice), priceMinor: v.displayPrice.amount }],
  );
}

/** How many tickets the counts add up to. */
export function meAddCount(counts: Readonly<Record<string, number>>): number {
  return Object.values(counts).reduce((n, c) => n + c, 0);
}

/** What is added, for which night, and the tickets' price before fees and tax; [] until something is counted. */
export function meAddSummary(
  choices: readonly MeAddChoice[],
  counts: Readonly<Record<string, number>>,
  nightWords: string,
  currency: string,
): readonly { readonly label: string; readonly value: string }[] {
  const picked = choices.filter((c) => (counts[c.variantId] ?? 0) > 0);
  if (picked.length === 0) return [];
  const subtotal = picked.reduce((n, c) => n + c.priceMinor * counts[c.variantId]!, 0);
  return [
    { label: "Adding", value: picked.map((c) => `${String(counts[c.variantId])} × ${c.name}`).join(" · ") },
    { label: "For", value: nightWords },
    { label: "Tickets", value: formatMoney({ amount: subtotal, currency }) },
  ];
}
